import {
  ACCOUNT_MENU_BANKS_COPY,
  ACCOUNT_MENU_HELP_COPY,
  ACCOUNT_MENU_SETTINGS_COPY,
  ACCOUNT_MENU_SUBSCRIPTION_COPY,
  NAV_DEBT_PAYOFF_COPY,
  NAV_HOME_COPY,
  NAV_INSIGHTS_COPY,
  NAV_PLAN_COPY,
  NAV_REVIEW_MY_DATA_COPY,
  NAV_UPCOMING_COPY,
  NAV_UPCOMING_LIST_COPY,
  NAV_UPCOMING_MONTH_COPY,
  NAV_UPCOMING_PAY_PERIODS_COPY,
  PLAN_TITLE_COPY,
} from "@fomofi/shared";
import {
  BarChart3,
  CalendarDays,
  Home,
  Landmark,
  Layers,
  LifeBuoy,
  Settings,
  Wallet,
  type LucideIcon,
} from "lucide-react";

export interface NavSection {
  to: string;
  label: string;
}

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * The existing pages this item stands for until its own spec lands (Anthony's
   * redesign, 2026-10-13: only Home is specced; Plan, Activity, Upcoming and
   * Insights are "to come"). Each page here lights the item, and when there is
   * more than one they render as tabs under the page header so every one stays a
   * click away. The first entry is `to`.
   */
  sections: NavSection[];
  /** Pages that light the item without a tab of their own. */
  alsoLights?: string[];
}

// The redesign's nav, shown only with the switch on (lib/redesignNav; today's nav is
// config/navItems). Single source of truth for the redesign nav: the desktop sidebar (AppSidebar), the
// tablet drawer (MobileSidebar), the phone tab bar and the section tabs all read it.
// The 12-item list became five (redesign spec, "The new navigation"), then four with
// Anthony's package (I1: Activity out). The account pages are in the account menu below.
export const REDESIGN_NAV_ITEMS: NavItem[] = [
  {
    to: "/home",
    label: NAV_HOME_COPY,
    icon: Home,
    sections: [{ to: "/home", label: NAV_HOME_COPY }],
    alsoLights: ["/dashboard"],
  },
  {
    to: "/plan",
    label: NAV_PLAN_COPY,
    icon: Layers,
    // Anthony's package (Plan Pages' nav, 2026-10-28): Plan › Your plan · Debt payoff.
    // Allocate, Budgets and Goals are retired (Paul, 2026-10-29): with the switch on their
    // routes land on Plan (lib/redesignRoutes REDESIGN_RETIRED). They still light Plan,
    // as /dashboard lights Home, for an address on its way through the redirect.
    sections: [
      { to: "/plan", label: PLAN_TITLE_COPY },
      { to: "/debt", label: NAV_DEBT_PAYOFF_COPY },
    ],
    alsoLights: ["/forecast", "/budgets", "/goals"],
  },
  // Activity (/transactions) left the nav (package I1): its page stays, reached from
  // Home's latest purchases ("All activity") and by its URL.
  {
    to: "/calendar",
    label: NAV_UPCOMING_COPY,
    icon: CalendarDays,
    // Workflow 3.12: Upcoming's views are Month · Pay periods · List. It lands on Month
    // (the calendar), as before CALL-14 (the default view is Needs Paul).
    sections: [
      { to: "/calendar", label: NAV_UPCOMING_MONTH_COPY },
      { to: "/paycheck", label: NAV_UPCOMING_PAY_PERIODS_COPY },
      { to: "/recurring", label: NAV_UPCOMING_LIST_COPY },
    ],
  },
  {
    to: "/insights",
    label: NAV_INSIGHTS_COPY,
    icon: BarChart3,
    sections: [
      { to: "/insights", label: NAV_INSIGHTS_COPY },
      { to: "/financial-analysis", label: NAV_REVIEW_MY_DATA_COPY },
    ],
  },
];

/** The account menu's links, in the spec's order. Dark mode and Log out are actions. */
export const ACCOUNT_LINKS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: "/settings", label: ACCOUNT_MENU_SETTINGS_COPY, icon: Settings },
  { to: "/banks", label: ACCOUNT_MENU_BANKS_COPY, icon: Landmark },
  { to: "/payment", label: ACCOUNT_MENU_SUBSCRIPTION_COPY, icon: Wallet },
  { to: "/help", label: ACCOUNT_MENU_HELP_COPY, icon: LifeBuoy },
];

function matches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/** The nav item a path belongs to, or undefined (account pages belong to none). */
export function navItemFor(pathname: string): NavItem | undefined {
  return REDESIGN_NAV_ITEMS.find(
    (item) =>
      item.sections.some((s) => matches(pathname, s.to)) ||
      (item.alsoLights ?? []).some((p) => matches(pathname, p)),
  );
}

/** Whether `item` is the lit one for `pathname`. */
export function navItemIsActive(item: NavItem, pathname: string): boolean {
  return navItemFor(pathname) === item;
}
